import React, { useState, useContext } from 'react';
import PropTypes from 'prop-types';
import { DefaultButton, PrimaryButton, IconButton, ChoiceGroup } from '@fluentui/react';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { DisclaimerBanner } from '@bingads-webui-campaign-react/help';
import { SummaryBar, Divider } from '@bingads-webui-campaign/reports-v4-summary-bar';
import { ReportsV4Footer } from '@bingads-webui-campaign/reports-v4-footer';
import { useReportsHistory } from '@bingads-webui-campaign/reports-v4-history';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { timeZones } from '@bingads-webui/timezone-details';
import { AccountSelectorCallout } from '../account-selector/account-selector-callout';
import { getScopeSelectorText } from '../../helpers/account-selector-helper';
import { BreadcrumbContainer } from '../summary-bar';
import { getDatePickerOptions, shouldShowAccountSelect } from './helper';

export function ReduceScopeView(props) {
  const {
    reportParam, reportSchema, onApply, onDismiss, classes, ctrLogger,
  } = props;
  const { reportName, scopeSelector } = reportParam;
  const i18n = useContext(I18nContext);
  const history = useReportsHistory();
  const { scope } = useContext(ScopeContext);

  const [dateRange, setDateRange] = useState('ThisMonth');
  const [scopes, setScopes] = useState(scopeSelector || {});
  const [showAccountSelectorCallout, setShowAccountSelectorCallout] = useState(false);

  function renderSummaryBar() {
    return (
      <SummaryBar>
        <Divider />
        <BreadcrumbContainer reportName={reportName} />
      </SummaryBar>
    );
  }

  function renderName() {
    return (
      <div className={`header ${classes.reportPreviewHeader}`}>
        <h2 id="ReportName" className="name">{reportName}</h2>
      </div>
    );
  }

  function handleApply() {
    const params = {
      dateRange: {
        key: dateRange,
      },
      scopes,
    };
    ctrLogger.click({ target: 'ReduceScopePageApply', extraData: { params } });
    onApply(params);
  }

  function handleDismiss() {
    ctrLogger.click({ target: 'ReduceScopePageDismiss' });
    onDismiss();
  }

  function renderStaticText() {
    return (
      <div className="reduce-scope-hint">
        <p className="title">{i18n.getString(_TL_('Report scope reductor'))}</p>
        <p className="desc">{i18n.getString(_TL_('Update default settings of report to reduce load time'))}</p>
      </div>
    );
  }

  function renderDateRangeSelector() {
    const options = getDatePickerOptions({ reportParam, reportSchema, i18n });
    if (options.length < 2) {
      return null;
    }
    return (
      <div className="date-range-select">
        <div className="title">{i18n.getString(_TL_('Date range'))}</div>
        <ChoiceGroup defaultSelectedKey={dateRange} options={options} onChange={(e, option) => { setDateRange(option.key); }} required />
      </div>
    );
  }

  function renderAccountScopeSelector() {
    if (!shouldShowAccountSelect({ reportSchema, scope })) {
      return null;
    }
    const selectedAccountCount = getScopeSelectorText({
      i18n, scopeSelectorInfo: scopes, reportSchema, history,
    });
    return (
      <div className="account-scope-select">
        <div className="title">{i18n.getString(_TL_('Select a subset of accounts/campaigns'))}</div>
        <p>
          <span>{selectedAccountCount}</span>
          <IconButton
            iconProps={{ iconName: 'Edit' }}
            title="Edit"
            ariaLabel="Edit"
            className={`${classes.icons} reduce-scope-account-scope`}
            onClick={() => setShowAccountSelectorCallout(true)}
          />
        </p>
        {showAccountSelectorCallout && <AccountSelectorCallout
          target=".reduce-scope-account-scope"
          getSelectedScope={() => scopes}
          reportSchema={reportSchema}
          onApply={(selected) => {
            setShowAccountSelectorCallout(false);
            setScopes(selected);
          }}
          onDismiss={() => setShowAccountSelectorCallout(false)}
        />}
      </div>
    );
  }

  function renderMain() {
    return (
      <div className="reduce-scope-main">
        <div className="selectors">
          {renderDateRangeSelector()}
          {renderAccountScopeSelector()}
        </div>
        <div className="button-group">
          <PrimaryButton text={i18n.getString(_TL_('Apply and Run'))} onClick={handleApply} />
          <DefaultButton text={i18n.getString(_TL_('Cancel'))} onClick={handleDismiss} />
        </div>
        {reportParam && <DisclaimerBanner timezone={timeZones[reportParam.timeZone]} />}
      </div>
    );
  }

  return (
    <section className={`reduce-scope-container ${classes.reduceScopeContainer}`}>
      {renderSummaryBar()}
      {renderName()}
      {renderStaticText()}
      {renderMain()}
      <ReportsV4Footer />
    </section>
  );
}

ReduceScopeView.propTypes = {
  reportParam: PropTypes.shape({}).isRequired,
  reportSchema: PropTypes.shape({}).isRequired,
  classes: PropTypes.shape({}).isRequired,
  onApply: PropTypes.func.isRequired,
  onDismiss: PropTypes.func.isRequired,
  ctrLogger: PropTypes.shape({}).isRequired,
};
